import { RiEyeLine, RiEyeOffLine, RiLoader4Line } from "@remixicon/react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { fetchClient } from "@/api/client";
import { $api } from "@/api/query";
import { BranchTable } from "@/components/integrations/branch-table";
import { BrandTable } from "@/components/integrations/brand-table";
import { SchedulerHealth } from "@/components/integrations/scheduler-health";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { fetchCurrentUser } from "@/lib/auth";
import { isOrgAdmin } from "@/lib/role";

export const Route = createFileRoute("/_app/integrations/prohandel")({
	// System page: org members are bounced to the dashboard (same pattern as
	// /admin). The backend enforces OrgAdmin on the config endpoints too.
	beforeLoad: async () => {
		const user = await fetchCurrentUser();
		if (!isOrgAdmin(user)) {
			throw redirect({ to: "/dashboard" });
		}
	},
	component: ProhandelIntegrationPage,
});

const filledClass = "border-primary/50 bg-primary/5";

function FilledInput({
	filled,
	...props
}: React.ComponentProps<typeof Input> & { filled?: boolean }) {
	return <Input {...props} className={filled ? filledClass : undefined} />;
}

function PasswordInput({
	filled,
	...props
}: React.ComponentProps<typeof Input> & { filled?: boolean }) {
	const [visible, setVisible] = useState(false);
	return (
		<div className="relative">
			<Input
				{...props}
				type={visible ? "text" : "password"}
				className={`pr-9 ${filled ? filledClass : ""}`}
			/>
			<button
				type="button"
				onClick={() => setVisible(!visible)}
				className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
			>
				{visible ? <RiEyeOffLine className="size-4" /> : <RiEyeLine className="size-4" />}
			</button>
		</div>
	);
}

interface ProhandelConfig {
	prohandelApiUrl?: string | null;
	prohandelApiKey?: string | null;
	prohandelPassword?: string | null;
	syncEnabled?: boolean;
	syncIntervalMinutes?: number;
	lastQuickSyncAt?: string | null;
	prohandelFilterMinLastSaleDate?: string | null;
	prohandelFilterSeasonNumbers?: number[] | null;
	prohandelFilterMinLabelPrice?: number | null;
	// ERP safety controls (DEV-978). Previously psql-only.
	prohandelWriteDryRun?: boolean;
	prohandelSaleBookingEnabled?: boolean;
	prohandelSalePaymentOption?: number | null;
	farfetchAcceptedTenantIds?: number[];
	prohandelFullWalkIntervalHours?: number;
}

interface SeasonOption {
	number: number;
	name?: string | null;
	articleCount: number;
}

const INTERVAL_OPTIONS = [
	{ value: 5, label: "Every 5 min" },
	{ value: 15, label: "Every 15 min" },
	{ value: 30, label: "Every 30 min (default)" },
	{ value: 60, label: "Every hour" },
	{ value: 180, label: "Every 3 hours" },
	{ value: 360, label: "Every 6 hours" },
	{ value: 720, label: "Every 12 hours" },
	{ value: 1440, label: "Daily" },
];

function ProhandelIntegrationPage() {
	const queryClient = useQueryClient();
	const { data, isLoading } = $api.useQuery("get", "/api/v1/integrations/config");
	const config = data as ProhandelConfig | undefined;

	const [apiUrl, setApiUrl] = useState("");
	const [apiKey, setApiKey] = useState("");
	const [password, setPassword] = useState("");
	const [syncEnabled, setSyncEnabled] = useState(false);
	const [syncIntervalMinutes, setSyncIntervalMinutes] = useState<number>(30);
	const [filterMinLastSale, setFilterMinLastSale] = useState<string>("");
	const [filterSeasonNumbers, setFilterSeasonNumbers] = useState<number[]>([]);
	const [filterMinLabelPrice, setFilterMinLabelPrice] = useState<string>("");
	// ERP safety controls. `writeDryRun` defaults to TRUE (paused) so a config
	// that has not loaded yet never renders as "writes are live".
	const [writeDryRun, setWriteDryRun] = useState(true);
	const [saleBookingEnabled, setSaleBookingEnabled] = useState(false);
	const [salePaymentOption, setSalePaymentOption] = useState<string>("");
	const [acceptedTenantIds, setAcceptedTenantIds] = useState<string>("");
	const [fullWalkIntervalHours, setFullWalkIntervalHours] = useState<number>(24);

	// Distinct seasons present in this org's article data. Populates the
	// multi-select; new seasons appear automatically after the next sync.
	const { data: seasonsData } = $api.useQuery("get", "/api/v1/prohandel/seasons" as never);
	const seasonOptions = (seasonsData as SeasonOption[] | undefined) ?? [];
	const [saving, setSaving] = useState(false);
	const [testing, setTesting] = useState(false);

	useEffect(() => {
		if (!config) return;
		setApiUrl(config.prohandelApiUrl ?? "");
		setApiKey(config.prohandelApiKey ?? "");
		setPassword(config.prohandelPassword ?? "");
		setSyncEnabled(config.syncEnabled ?? false);
		setSyncIntervalMinutes(config.syncIntervalMinutes ?? 30);
		setFilterMinLastSale(config.prohandelFilterMinLastSaleDate ?? "");
		setFilterSeasonNumbers(config.prohandelFilterSeasonNumbers ?? []);
		setFilterMinLabelPrice(
			config.prohandelFilterMinLabelPrice != null
				? String(config.prohandelFilterMinLabelPrice)
				: "",
		);
		setWriteDryRun(config.prohandelWriteDryRun ?? true);
		setSaleBookingEnabled(config.prohandelSaleBookingEnabled ?? false);
		setSalePaymentOption(
			config.prohandelSalePaymentOption != null ? String(config.prohandelSalePaymentOption) : "",
		);
		setAcceptedTenantIds((config.farfetchAcceptedTenantIds ?? []).join(", "));
		setFullWalkIntervalHours(config.prohandelFullWalkIntervalHours ?? 24);
	}, [config]);

	function toggleSeasonNumber(n: number) {
		setFilterSeasonNumbers((prev) =>
			prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n],
		);
	}

	const lastQuickSyncAt = config?.lastQuickSyncAt ?? null;
	const nextSyncAt =
		syncEnabled && lastQuickSyncAt
			? new Date(new Date(lastQuickSyncAt).getTime() + syncIntervalMinutes * 60_000)
			: null;

	function invalidate() {
		queryClient.invalidateQueries({ queryKey: ["get", "/api/v1/integrations/config"] });
	}

	async function handleSave() {
		setSaving(true);
		try {
			const body: Record<string, unknown> = {
				prohandelApiUrl: apiUrl || null,
				prohandelApiKey: apiKey || null,
				prohandelPassword: password || null,
				syncEnabled,
				syncIntervalMinutes,
			};
			// Always send these three, using null to clear. Omitting a key means
			// "keep existing" server-side, so the old truthiness guards made an
			// emptied filter unclearable: the save succeeded and the filter
			// stayed live. Clearing is a LOOSENING and re-admits purged
			// articles, so the backend kicks a re-sync off the back of it.
			body.prohandelFilterMinLastSaleDate = filterMinLastSale || null;
			body.prohandelFilterSeasonNumbers =
				filterSeasonNumbers.length > 0 ? filterSeasonNumbers : null;
			const parsedMinPrice = Number(filterMinLabelPrice);
			body.prohandelFilterMinLabelPrice =
				filterMinLabelPrice && Number.isFinite(parsedMinPrice) && parsedMinPrice >= 0
					? parsedMinPrice
					: null;
			// ERP safety controls. The tenant list is parsed from a comma-separated
			// string; an unparseable or empty list is OMITTED rather than sent,
			// because an empty list refuses every incoming Farfetch order and the
			// backend would (correctly) 400 on it.
			body.prohandelWriteDryRun = writeDryRun;
			body.prohandelSaleBookingEnabled = saleBookingEnabled;
			const parsedPayOpt = Number(salePaymentOption);
			body.prohandelSalePaymentOption =
				salePaymentOption && Number.isFinite(parsedPayOpt) && parsedPayOpt > 0
					? parsedPayOpt
					: null;
			const tenants = acceptedTenantIds
				.split(",")
				.map((t) => Number(t.trim()))
				.filter((t) => Number.isFinite(t) && t > 0);
			if (tenants.length > 0) {
				body.farfetchAcceptedTenantIds = tenants;
			}
			body.prohandelFullWalkIntervalHours = fullWalkIntervalHours;

			const { error } = await fetchClient.PUT(
				"/api/v1/integrations/config" as never,
				{ body } as never,
			);
			if (error) {
				toast.error("Failed to save Prohandel config");
				return;
			}
			toast.success("Prohandel config saved");
			invalidate();
		} finally {
			setSaving(false);
		}
	}

	async function handleTest() {
		setTesting(true);
		try {
			const { data: result, error } = await fetchClient.POST(
				"/api/v1/integrations/test-prohandel" as never,
			);
			if (error) {
				toast.error("Test request failed");
				return;
			}
			const r = result as { success: boolean; message: string };
			if (r.success) toast.success(r.message);
			else toast.error(r.message);
		} finally {
			setTesting(false);
		}
	}

	return (
		<div className="space-y-4">
			<div>
				<h1 className="text-2xl font-bold tracking-tight">Prohandel</h1>
				<p className="text-muted-foreground">
					Connect to the Prohandel ERP to sync articles and stock data.
				</p>
			</div>

			{isLoading ? (
				<div className="flex items-center gap-2 text-muted-foreground">
					<RiLoader4Line className="size-4 animate-spin" />
					Loading configuration...
				</div>
			) : (
				<Tabs defaultValue="credentials" className="max-w-2xl">
					<TabsList>
						<TabsTrigger value="credentials">Credentials</TabsTrigger>
						<TabsTrigger value="branches">Branches</TabsTrigger>
						<TabsTrigger value="sync">Sync</TabsTrigger>
						<TabsTrigger value="filters">Filters</TabsTrigger>
					</TabsList>

					<TabsContent value="credentials">
						<Card>
							<CardHeader>
								<CardTitle>API credentials</CardTitle>
								<CardDescription>Prohandel API URL, key, and password.</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="space-y-2">
									<Label htmlFor="ph-url">API URL</Label>
									<FilledInput
										id="ph-url"
										value={apiUrl}
										onChange={(e) => setApiUrl(e.target.value)}
										placeholder="https://prohandel.dev/api/v3"
										filled={!!apiUrl}
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="ph-key">API Key</Label>
									<PasswordInput
										id="ph-key"
										value={apiKey}
										onChange={(e) => setApiKey(e.target.value)}
										placeholder="Enter API key"
										filled={!!apiKey}
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="ph-pass">Password</Label>
									<PasswordInput
										id="ph-pass"
										value={password}
										onChange={(e) => setPassword(e.target.value)}
										placeholder="Enter password (optional)"
										filled={!!password}
									/>
								</div>
								<div className="flex gap-2 pt-2">
									<Button onClick={handleSave} disabled={saving}>
										{saving && <RiLoader4Line className="mr-1 size-4 animate-spin" />}
										Save
									</Button>
									<Button variant="outline" onClick={handleTest} disabled={testing}>
										{testing ? <RiLoader4Line className="mr-1 size-4 animate-spin" /> : null}
										Test connection
									</Button>
								</div>
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="branches">
						<Card>
							<CardHeader>
								<CardTitle>Branches</CardTitle>
								<CardDescription>
									Choose which Prohandel branches contribute stock to the catalogue.
								</CardDescription>
							</CardHeader>
							<CardContent>
								<BranchTable />
							</CardContent>
						</Card>
					</TabsContent>

					<TabsContent value="sync">
						<Card>
							<CardHeader>
								<CardTitle>Scheduled sync</CardTitle>
								<CardDescription>
									Automatically pull changes from Prohandel on a schedule.
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="flex items-center justify-between">
									<Label className="font-medium">Enable scheduled sync</Label>
									<Switch checked={syncEnabled} onCheckedChange={setSyncEnabled} />
								</div>
								<div className="space-y-2">
									<Label>Interval</Label>
									<Select
										value={String(syncIntervalMinutes)}
										onValueChange={(v) => setSyncIntervalMinutes(Number(v))}
										disabled={!syncEnabled}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{INTERVAL_OPTIONS.map((o) => (
												<SelectItem key={o.value} value={String(o.value)}>
													{o.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								{syncEnabled && (
									<div className="text-xs text-muted-foreground space-y-0.5">
										{lastQuickSyncAt && (
											<div>Last sync: {new Date(lastQuickSyncAt).toLocaleString()}</div>
										)}
										{nextSyncAt && <div>Next sync: {nextSyncAt.toLocaleString()}</div>}
									</div>
								)}
								<Button onClick={handleSave} disabled={saving}>
									{saving && <RiLoader4Line className="mr-1 size-4 animate-spin" />}
									Save
								</Button>
							</CardContent>
						</Card>

						<Card className="mt-4">
							<CardHeader>
								<CardTitle>ERP safety</CardTitle>
								<CardDescription>
									Controls over what Burresi writes back to Prohandel. These were previously
									changeable only by hand on the server.
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								{/* State first and unmissable: during the CloudStock incident the
								    operator had no way to see whether writes were paused. */}
								<div
									className={`rounded-md border px-3 py-2 text-sm ${
										writeDryRun
											? "border-amber-500/50 bg-amber-500/10"
											: "border-emerald-500/50 bg-emerald-500/10"
									}`}
									data-testid="erp-write-state"
								>
									{writeDryRun
										? "ERP writes are PAUSED. Stock is still reserved in the ledger, but no transfer or sale reaches Prohandel."
										: "ERP writes are LIVE. Transfers and sales are posted to Prohandel."}
								</div>

								<div className="flex items-center justify-between">
									<div>
										<Label className="font-medium">Pause all ERP writes</Label>
										<p className="text-xs text-muted-foreground">
											Master switch over transfers, releases and sale bookings.
										</p>
									</div>
									<Switch
										checked={writeDryRun}
										onCheckedChange={setWriteDryRun}
										aria-label="Pause all ERP writes"
									/>
								</div>

								<div className="flex items-center justify-between">
									<div>
										<Label className="font-medium">Book sales automatically</Label>
										<p className="text-xs text-muted-foreground">
											Posts the saleInstruction once a transfer is confirmed. Also requires ERP
											writes to be live.
										</p>
									</div>
									<Switch
										checked={saleBookingEnabled}
										onCheckedChange={setSaleBookingEnabled}
										aria-label="Book sales automatically"
									/>
								</div>

								<div className="space-y-1">
									<Label htmlFor="sale-payment-option">Payment condition</Label>
									<Input
										id="sale-payment-option"
										type="number"
										min={1}
										value={salePaymentOption}
										onChange={(e) => setSalePaymentOption(e.target.value)}
										placeholder="Leave empty for Prohandel's default"
									/>
								</div>

								<div className="space-y-1">
									<Label htmlFor="accepted-tenants">Accepted Farfetch tenants</Label>
									<Input
										id="accepted-tenants"
										value={acceptedTenantIds}
										onChange={(e) => setAcceptedTenantIds(e.target.value)}
										placeholder="10000, 180000, 69000"
									/>
									<p className="text-xs text-muted-foreground">
										Comma-separated. 10000 = FF.com, 69000 = TMALL, 180000 = RLUX-Coupang. An order
										from a tenant not listed here is refused.
									</p>
								</div>

								<div className="space-y-1">
									<Label htmlFor="full-walk-interval">Full stock walk every (hours)</Label>
									<Input
										id="full-walk-interval"
										type="number"
										min={6}
										max={168}
										value={fullWalkIntervalHours}
										onChange={(e) => setFullWalkIntervalHours(Number(e.target.value))}
									/>
									<p className="text-xs text-muted-foreground">
										The only signal that catches sold-out stock: Prohandel's delta feed never
										reports a sale. Raising this widens the window in which sold-out items stay
										published to Farfetch. 6-168.
									</p>
								</div>

								<Button onClick={handleSave} disabled={saving}>
									{saving && <RiLoader4Line className="mr-1 size-4 animate-spin" />}
									Save
								</Button>
							</CardContent>
						</Card>

						<div className="mt-4">
							<SchedulerHealth />
						</div>
					</TabsContent>

					<TabsContent value="filters">
						<Card>
							<CardHeader>
								<CardTitle>Sync filters</CardTitle>
								<CardDescription>
									Restrict which Prohandel articles get pulled into the catalogue.
								</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="space-y-2">
									<Label htmlFor="filter-min-last-sale">Only sync articles last sold after</Label>
									<Input
										id="filter-min-last-sale"
										type="date"
										value={filterMinLastSale}
										onChange={(e) => setFilterMinLastSale(e.target.value)}
									/>
									<p className="text-xs text-muted-foreground">
										Leave empty to sync articles from any date.
									</p>
								</div>
								<div className="space-y-2">
									<Label>Only sync these seasons</Label>
									{seasonOptions.length === 0 ? (
										<p className="text-xs text-muted-foreground">
											No seasons discovered yet. Run a sync to populate this list.
										</p>
									) : (
										<div className="rounded-md border max-h-60 overflow-y-auto divide-y">
											{seasonOptions.map((s) => {
												const id = `season-${s.number}`;
												const checked = filterSeasonNumbers.includes(s.number);
												return (
													<label
														key={s.number}
														htmlFor={id}
														className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/40"
													>
														<Checkbox
															id={id}
															checked={checked}
															onCheckedChange={() => toggleSeasonNumber(s.number)}
														/>
														<span className="text-sm font-mono tabular-nums">{s.number}</span>
														<span className="text-sm flex-1 truncate">
															{s.name || (
																<span className="text-muted-foreground italic">
																	no name in Prohandel
																</span>
															)}
														</span>
														<span className="text-xs text-muted-foreground tabular-nums">
															{s.articleCount.toLocaleString()}
														</span>
													</label>
												);
											})}
										</div>
									)}
									<p className="text-xs text-muted-foreground">
										Filter by Prohandel's stable season number. Leave empty for all seasons.
									</p>
								</div>
								<div className="space-y-2">
									<Label htmlFor="filter-min-label-price">
										Only sync articles with label price ≥
									</Label>
									<Input
										id="filter-min-label-price"
										type="number"
										min="0"
										step="0.01"
										value={filterMinLabelPrice}
										onChange={(e) => setFilterMinLabelPrice(e.target.value)}
										placeholder="e.g. 50"
									/>
									<p className="text-xs text-muted-foreground">
										Matches against <code>label_price</code> from Prohandel. Articles with no price
										are kept regardless. Leave empty for no price floor.
									</p>
								</div>
								<Button onClick={handleSave} disabled={saving}>
									{saving && <RiLoader4Line className="mr-1 size-4 animate-spin" />}
									Save
								</Button>
							</CardContent>
						</Card>

						<Card className="mt-4">
							<CardHeader>
								<CardTitle>Brands</CardTitle>
								<CardDescription>
									Switch brands on or off, like branches. Deactivating removes the brand's articles
									from the catalogue (their edits included); re-activating re-imports them from
									Prohandel (~40 min).
								</CardDescription>
							</CardHeader>
							<CardContent>
								<BrandTable />
							</CardContent>
						</Card>
					</TabsContent>
				</Tabs>
			)}
		</div>
	);
}
